import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Table, Card, Typography, Button, message, Row, Col, Statistic, Tag } from 'antd';
import { ArrowLeftOutlined, PrinterOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { apiClient } from '../../../shared/api/AllApi';
import { formatCurrency, toApiNumber } from '../../../utils/formatters';
import { orderDocNo } from '../../../shared/lib/documentNumbers';
import DateRangeFilter from '../../../shared/ui/DateRangeFilter';
import useTablePagination from '../../../shared/ui/useTablePagination';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

const { Title } = Typography;

const CustomerOrderHistory = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const { pagination } = useTablePagination();
  const [orders, setOrders] = useState([]);
  const [customerInfo, setCustomerInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState([null, null]);
  const [totalWeight, settotalWeightOrders] = useState(0);

  const handleDateChange = async (dates) => {
    setDateRange(dates);

    if (dates && dates[0] && dates[1]) {
      const start_date = dayjs(dates[0]).format('YYYY-MM-DD');
      const end_date = dayjs(dates[1]).format('YYYY-MM-DD');

      try {
        await fetchCustomerOrders(start_date, end_date);
      } catch (error) {
        console.error('Error fetching filtered orders:', error);
      }
    }
  };

  const fetchCustomerOrders = useCallback(async (start_date, end_date) => {
    try {
      const response = await apiClient.get(`/customers/${id}/orders`, {
        params: { start_date, end_date },
      });
      setOrders(response.data.orders);
      settotalWeightOrders(response.data.total_weight || 0);
      setCustomerInfo(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching orders:', error);
      message.error(t('fetchError', 'Error loading data'));
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    fetchCustomerOrders();
  }, [fetchCustomerOrders]);

  const columns = [
    {
      title: '#',
      key: 'index',
      render: (_, __, index) => index + 1,
      width: 70,
    },
    {
      title: <b>{t('orderNo', 'Order #')}</b>,
      key: 'document_no',
      width: 150,
      render: (_, r) => (
        <span style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700 }}>
          {orderDocNo(r)}
        </span>
      ),
    },
    {
      title: <b>{t('orderDate', 'Order Date')}</b>,
      dataIndex: 'created_at',
      key: 'created_at',
      render: function(text) {
        if (!text) return '';
        return dayjs(text).utc().format('DD/MM/YYYY HH:mm:ss'); // Format as DD/MM/YYYY HH:mm:ss in UTC
      },
    },
    {
      title: <b>{t('orderWeight', 'Order Weight')}</b>,
      dataIndex: 'total_order_weight',
      key: 'total_order_weight',
       render: (text) => {
        const raw = parseFloat((text || '0').toString().replace(/,/g, ''));
        return `${raw.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 1 })} kg`;
      },
    },
    {
      title: <b>{t('totalPrice', 'Total Price')}</b>,
      dataIndex: 'total_price',
      key: 'total_price',
      render: (text) => formatCurrency(text),
    },
    {
      title: <b>{t('amountPaid', 'Amount Paid')}</b>,
      dataIndex: 'net_paid',
      key: 'net_paid',
      render: (text) => formatCurrency(text),
    },
    {
      title: <b>{t('refunded', 'Refunded')}</b>,
      dataIndex: 'refunded_amount',
      key: 'refunded_amount',
      render: (text) => <span style={{ color: '#cf1322' }}>{formatCurrency(text)}</span>,
    },
    {
      title: <b>{t('status', 'Status')}</b>,
      dataIndex: 'status',
      key: 'status',
      render: (value, record) => {
        const statusMeta = {
          confirmed: { color: 'green', label: t('statusConfirmed', 'Confirmed') },
          completed: { color: 'blue', label: t('statusCompleted', 'Completed') },
          cancelled: { color: 'red', label: t('statusCancelled', 'Cancelled') },
          returned: { color: 'orange', label: t('statusReturned', 'Returned') },
          pending: { color: 'gold', label: t('statusPending', 'Pending') },
          draft: { color: 'default', label: t('statusDraft', 'Draft') },
        };
        const meta = statusMeta[value] || { color: 'default', label: value || t('statusConfirmed', 'Confirmed') };
        return (
          <div>
            <Tag color={meta.color}>{meta.label}</Tag>
            {record.cancellation_reason && <div style={{ fontSize: 11, color: '#8c8c8c' }}>{record.cancellation_reason}</div>}
          </div>
        );
      },
    },
    {
      title: <b>{t('outstandingBalance', 'Outstanding Balance')}</b>,
      dataIndex: 'outstanding_balance',
      key: 'outstanding_balance',
      render: (text) => formatCurrency(text),
    },
    {
      title: <b>{t('actions', 'Actions')}</b>,
      key: 'actions',
      render: (_, record) => (
        <>
          <Link to={`/customer/${id}/order/${record.id}`} style={{ marginLeft: 8 }}>
            <Button type="primary">{t('view', 'View')}</Button>
          </Link>
          <Link to={`/orders/${record.id}/view`} target="_blank">
            <Button icon={<PrinterOutlined />} style={{ background: '#faad14', color: '#fff', border: 'none', marginRight: 0 }} />
          </Link>
        </>
      ),
    },
  ];

  // Defensive: always treat dateRange as array, fallback to [null, null] if null
  const safeDateRange = Array.isArray(dateRange) ? dateRange : [null, null];

  const filteredOrders = orders.filter(order => {
    if (!safeDateRange[0] || !safeDateRange[1]) return true;
    const orderDate = dayjs(order.created_at);
    return orderDate.isAfter(safeDateRange[0].startOf('day').subtract(1, 'ms')) && orderDate.isBefore(safeDateRange[1].endOf('day').add(1, 'ms'));
  });

  // Use filteredOrders for statistics if a date range is selected
  const statsOrders = (safeDateRange[0] && safeDateRange[1]) ? filteredOrders : orders;
  const statsTotalPrice = toApiNumber(customerInfo?.total_orders, statsOrders.reduce((sum, order) => sum + toApiNumber(order.total_price, 0), 0));
  const statsTotalPaid = toApiNumber(customerInfo?.total_paid, 0);
  const statsTotalOutstanding = toApiNumber(customerInfo?.total_outstanding, 0);
  const statsTotalWeight = totalWeight || 0;

  if (!id) {
    return (
      <div style={{ textAlign: 'center', marginTop: '50px' }}>
        <h1>{t("invalidCustomerId", "Invalid or missing customer ID")}</h1>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', direction: 'rtl' }}>
      <Card
        style={{
          marginBottom: '20px',
          background: '#f0f2f5',
          borderRadius: '15px',
          padding: '20px',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Title level={3} style={{ margin: 0 }}>
            {customerInfo ? `${customerInfo.name}` : t('orderHistory', 'Order History')}
          </Title>
          <Link to="/customers">
            <Button type="primary" icon={<ArrowLeftOutlined />}>
              {t('back', 'Back')}
            </Button>
          </Link>
        </div>
      </Card>

      <Row gutter={[24, 24]} style={{ marginBottom: '20px' }}>
        <Col xs={24} sm={12} md={12} lg={4}>
          <Card style={{ background: '#f6ffed', borderRadius: '10px', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Statistic
              title={t('totalOrders', 'Total Orders')}
              value={statsOrders.length}
              style={{ textAlign: 'center' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={12} lg={5}>
          <Card style={{ background: '#e6f7ff', borderRadius: '10px', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Statistic
              title={t('totalPrice', 'Total Price')}
              value={formatCurrency(statsTotalPrice)}
              style={{ textAlign: 'center' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={12} lg={5}>
          <Card style={{ background: '#d9f7be', borderRadius: '10px', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Statistic
              title={t('totalPaid', 'Total Paid')}
              value={formatCurrency(statsTotalPaid)}
              style={{ textAlign: 'center' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={12} lg={5}>
          <Card style={{ background: '#fff1f0', borderRadius: '10px', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Statistic
              title={t('totalOutstanding', 'Total Outstanding')}
              value={formatCurrency(statsTotalOutstanding)}
              style={{ textAlign: 'center' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={12} lg={5}>
          <Card style={{ background: '#fff7e6', borderRadius: '10px', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Statistic
              title={t('totalWeight', 'Total Weight')}
              value={statsTotalWeight}
              suffix="kg"
              style={{ textAlign: 'center' }}
            />
          </Card>
        </Col>
      </Row>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
        <div style={{ width: 'fit-content' }}>
          <DateRangeFilter
            value={safeDateRange}
            onChange={handleDateChange}
            showTime={false}
            showPresets={false}
          />
        </div>
      </div>
      <Card
        style={{
          background: '#ffffff',
          borderRadius: '15px',
          padding: '20px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
        }}
      >
        <Table
          dataSource={orders}
          columns={columns}
          pagination={pagination}
          loading={loading}
          rowKey={(record) => record.id}
        />
      </Card>
    </div>
  );
};

export default CustomerOrderHistory;

